import { defineRecipe } from '@plurall/elo-v3';

// ─── Container ────────────────────────────────────────────────────────────────

export const containerRecipe = defineRecipe({
  className: 'table-container',
  base: {
    width: '100%',
    borderRadius: 'shape.8',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'border.secondary',
    backgroundColor: 'background.primary',
    overflow: 'hidden',
  },
});

// ─── Wrapper (scroll) ─────────────────────────────────────────────────────────

export const wrapperRecipe = defineRecipe({
  className: 'table-wrapper',
  base: {
    overflowX: 'auto',
  },
});

// ─── Table root ───────────────────────────────────────────────────────────────

export const tableRootRecipe = defineRecipe({
  className: 'table',
  base: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 'font-size-small',
    color: 'text.primary',
  },
});

// ─── Header ───────────────────────────────────────────────────────────────────

export const headerRecipe = defineRecipe({
  className: 'table-header',
  base: {
    backgroundColor: 'background.secondary',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: 'border.secondary',
  },
});

export const headerRowRecipe = defineRecipe({
  className: 'table-header-row',
  base: {
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: 'border.secondary',
  },
});

export const headerCellRecipe = defineRecipe({
  className: 'table-header-cell',
  base: {
    padding: 'space.16',
    paddingLeft: 'space.16',
    paddingRight: 'space.16',
    textAlign: 'left',
    fontWeight: 'font-weight-semi-bold',
    color: 'text.primary',
    position: 'relative',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
    borderRightWidth: '1px',
    borderRightStyle: 'solid',
    borderRightColor: 'border.secondary',

    '&:first-child': { paddingLeft: 'space.24' },
    '&:last-child': { borderRight: 'none', paddingRight: 'space.24' },
    '&[rowspan]': { backgroundColor: 'background.secondary', fontWeight: 'font-weight-bold' },

    '&[data-sortable="true"]': {
      cursor: 'pointer',
      userSelect: 'none',
      _hover: { backgroundColor: 'action.hover.brand' },
    },
    '&[data-sorted="true"]': { backgroundColor: 'action.selected.brand.alpha' },
  },
});

// ─── Sortable header content ──────────────────────────────────────────────────

export const headerSortableRecipe = defineRecipe({
  className: 'table-header-sortable',
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: 'space.4',
    cursor: 'pointer',
    userSelect: 'none',
  },
});

export const sortIndicatorRecipe = defineRecipe({
  className: 'table-sort-indicator',
  base: {
    fontSize: 'font-size-xsmall',
    color: 'text.secondary',
    marginLeft: 'auto',
  },
});

// ─── Body ─────────────────────────────────────────────────────────────────────

export const bodyRecipe = defineRecipe({
  className: 'table-body',
  base: {
    backgroundColor: 'background.primary',
  },
});

export const rowRecipe = defineRecipe({
  className: 'table-body-row',
  base: {
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: 'border.secondary',
    transition: 'background-color 150ms ease-in-out',

    _hover: { backgroundColor: 'action.hover.brand' },
    '&:last-child': { borderBottom: 'none' },

    '&[data-selected="true"]': {
      backgroundColor: 'action.selected.brand.alpha',
      borderLeftWidth: '3px',
      borderLeftStyle: 'solid',
      borderLeftColor: 'action.selected.brand',
      _hover: { backgroundColor: 'action.selected.brand.alpha' },
    },
  },
});

export const cellRecipe = defineRecipe({
  className: 'table-body-cell',
  base: {
    padding: 'space.16',
    color: 'text.primary',
    verticalAlign: 'middle',

    '&:first-child': { paddingLeft: 'space.24' },
    '&:last-child': { paddingRight: 'space.24' },
  },

  variants: {
    variant: {
      default: {},
      selection: {
        width: 'size.40',
        textAlign: 'center',
        paddingInline: 'space.8',
      },
      empty: {
        textAlign: 'center',
        padding: 'space.32',
        color: 'text.secondary',
        fontStyle: 'italic',
      },
    },
  },

  defaultVariants: { variant: 'default' },
});

// ─── Toolbar ──────────────────────────────────────────────────────────────────

export const toolbarRecipe = defineRecipe({
  className: 'table-toolbar',
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 'space.16',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: 'border.secondary',

    '@media (max-width: 768px)': {
      flexDirection: 'column',
      gap: 'space.8',
      alignItems: 'stretch',
    },
  },
});

export const searchRecipe = defineRecipe({
  className: 'table-search',
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: 'space.8',
    flex: 1,
    minWidth: 0,
  },
});

export const columnVisibilityRecipe = defineRecipe({
  className: 'table-column-visibility',
  base: {
    display: 'flex',
    alignItems: 'center',
    width: 'fit-content',
    minWidth: 'fit-content',
    maxWidth: 'none',
    overflow: 'visible',
  },
});

// ─── Pagination ───────────────────────────────────────────────────────────────

export const paginationRecipe = defineRecipe({
  className: 'table-pagination',
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBlock: 'space.16',
    paddingInline: 'space.24',
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderTopColor: 'border.secondary',
    backgroundColor: 'background.secondary',

    '@media (max-width: 768px)': {
      flexDirection: 'column',
      gap: 'space.8',
      alignItems: 'stretch',
    },
  },
});

export const paginationInfoRecipe = defineRecipe({
  className: 'table-pagination-info',
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: 'space.8',
  },
});

export const paginationTextRecipe = defineRecipe({
  className: 'table-pagination-text',
  base: {
    fontSize: 'font-size-small',
    color: 'text.secondary',
    whiteSpace: 'nowrap',
  },
});

export const paginationControlsRecipe = defineRecipe({
  className: 'table-pagination-controls',
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: 'space.8',

    '@media (max-width: 768px)': { justifyContent: 'center' },
  },
});

export const paginationButtonRecipe = defineRecipe({
  className: 'table-pagination-button',
  base: {
    fontSize: 'font-size-small',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 'size.32',
    width: 'size.32',
    padding: 0,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'border.primary',
    borderRadius: 'shape.4',
    backgroundColor: 'background.primary',
    color: 'text.primary',
    fontWeight: 'font-weight-regular',
    cursor: 'pointer',
    transition: 'all 150ms ease-in-out',
    outline: 'none',

    _hover: {
      backgroundColor: 'action.hover.brand',
      borderColor: 'border.brand.primary',
    },
    _focusVisible: {
      borderColor: 'action.focus.ring.primary',
      boxShadow: '0 0 0 2px {colors.background.brand.soft}',
    },
    _disabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
      backgroundColor: 'background.secondary',
    },
  },
});
